import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { feedbackApi } from '../lib/api';
import { toast } from 'sonner';
import { MessageSquarePlus, Lightbulb, FileQuestion, Bug, Send, CheckCircle } from 'lucide-react';

const FEEDBACK_TYPES = [
  { value: 'feedback', label: 'General Feedback', icon: MessageSquarePlus, color: 'text-blue-500', bg: 'bg-blue-50' },
  { value: 'content_request', label: 'Content Request', icon: FileQuestion, color: 'text-amber-500', bg: 'bg-amber-50' },
  { value: 'tool_request', label: 'Tool / Feature Request', icon: Lightbulb, color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'bug_report', label: 'Report a Bug', icon: Bug, color: 'text-red-500', bg: 'bg-red-50' },
];

export default function FeedbackForm({ onClose }) {
  const [type, setType] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!type || !subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await feedbackApi.submit({ type, subject, message });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-white border-stone-200" data-testid="feedback-success">
        <CardContent className="py-10 text-center">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="font-heading text-lg font-bold text-stone-900 mb-2">Thank You!</h3>
          <p className="text-sm text-stone-600 mb-4">Your feedback has been submitted. Our team will review it and use it to improve RaiseRoot.</p>
          <p className="text-xs text-stone-500 mb-6">
            Need immediate help? Contact <a href="mailto:info@strategicfundraisingconsulting.com" className="text-primary hover:underline">info@strategicfundraisingconsulting.com</a>
          </p>
          {onClose && (
            <Button variant="outline" onClick={onClose} data-testid="feedback-close-btn">Close</Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-stone-200" data-testid="feedback-form">
      <CardHeader>
        <CardTitle className="font-heading text-lg text-[#0A0A0A] flex items-center gap-2">
          <MessageSquarePlus className="w-5 h-5 text-primary" />
          Share Your Feedback
        </CardTitle>
        <CardDescription>Help us improve RaiseRoot. Request content, suggest tools, or report issues.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div>
            <Label className="text-stone-700 mb-2 block">What type of feedback?</Label>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_TYPES.map((ft) => {
                const Icon = ft.icon;
                return (
                  <button
                    key={ft.value}
                    type="button"
                    onClick={() => setType(ft.value)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      type === ft.value
                        ? 'border-primary bg-primary/5'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`feedback-type-${ft.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg ${ft.bg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${ft.color}`} />
                      </div>
                      <span className="text-xs font-medium text-stone-700">{ft.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-1">
            <Label htmlFor="feedback-subject" className="text-stone-700">Subject</Label>
            <Input
              id="feedback-subject"
              placeholder="Brief summary of your feedback"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className="bg-white border-stone-200"
              data-testid="feedback-subject"
            />
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label htmlFor="feedback-message" className="text-stone-700">Details</Label>
            <Textarea
              id="feedback-message"
              placeholder={
                type === 'content_request' ? "What type of content or template would you like us to add?"
                : type === 'tool_request' ? "Describe the tool or feature you'd like to see..."
                : type === 'bug_report' ? "What happened? What did you expect to happen?"
                : "Tell us what's on your mind..."
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={4}
              className="bg-white border-stone-200 resize-none"
              data-testid="feedback-message"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11 bg-primary hover:bg-primary-800 text-white"
            disabled={loading || !type}
            data-testid="feedback-submit-btn"
          >
            {loading ? <div className="w-5 h-5 spinner" /> : <><Send className="w-4 h-4 mr-2" /> Submit Feedback</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
